// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

// TruncatedText — long free text in a table cell (owner 2026-10-01: the enrollment queue's clinical
// notes and last review). The cell shows the first `max` characters; when that cuts the text, hovering
// or focusing it opens a tooltip with the COMPLETE text (and an optional heading, e.g. author · time).
// The tooltip is position: fixed so a scrolling or overflow-hidden table never clips it.

import { useEffect, useId, useRef, useState, type CSSProperties } from 'react'

const TIP_WIDTH = 360
const SR_ONLY: CSSProperties = { position: 'absolute', width: 1, height: 1, padding: 0, margin: -1, overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap', border: 0 }

export function truncate(text: string, max: number): { shown: string; cut: boolean } {
  const flat = text.replace(/\s+/g, ' ').trim()
  return flat.length <= max ? { shown: flat, cut: false } : { shown: flat.slice(0, max).trimEnd() + '…', cut: true }
}

export default function TruncatedText({ text, max = 60, heading, label }: {
  text?: string | null
  max?: number
  /** Shown above the full text in the tooltip — always, so the cell can stay short. */
  heading?: string | null
  /** What the text is, for screen readers ("Clinical notes"). */
  label: string
}) {
  const ref = useRef<HTMLSpanElement>(null)
  const [at, setAt] = useState<{ left: number; top: number; above: boolean } | null>(null)
  const describedBy = useId()
  // Review L16: a touch screen has no hover, and a tap on the cell opened the row behind it instead.
  // A TOUCH tap on a cell with a tooltip toggles the tooltip and stays in the cell; a mouse click
  // still opens the record as before.
  const touch = useRef(false)
  const openAtDown = useRef(false)   // a tap also focuses (which opens): toggle from the state BEFORE it
  // The tooltip is fixed to where the cell WAS: when scrolling or resizing MOVES the cell, it closes.
  // Only when it moved (L16): a touch tap inside a scrollable table can fire a scroll event that moves
  // nothing, which closed the tooltip the tap had just opened.
  useEffect(() => {
    if (!at) return
    const start = ref.current?.getBoundingClientRect()
    const close = () => {
      const now = ref.current?.getBoundingClientRect()
      if (!start || !now || Math.abs(now.top - start.top) > 2 || Math.abs(now.left - start.left) > 2) setAt(null)
    }
    window.addEventListener('scroll', close, true)
    window.addEventListener('resize', close)
    return () => { window.removeEventListener('scroll', close, true); window.removeEventListener('resize', close) }
  }, [at])
  if (!text || !text.trim()) return <span style={{ color: 'var(--color-text-mute)' }}>—</span>
  const { shown, cut } = truncate(text, max)
  const tip = cut || !!heading

  const open = () => {
    const r = ref.current?.getBoundingClientRect()
    if (!r) return
    const above = r.bottom > window.innerHeight * 0.6
    setAt({ left: Math.max(8, Math.min(r.left, window.innerWidth - TIP_WIDTH - 8)), top: above ? r.top - 6 : r.bottom + 6, above })
  }
  const close = () => setAt(null)
  const box: CSSProperties = {
    position: 'fixed', left: at?.left, top: at?.top, transform: at?.above ? 'translateY(-100%)' : undefined,
    zIndex: 2000, width: TIP_WIDTH, maxHeight: 320, overflowY: 'auto', padding: '8px 10px', borderRadius: 8,
    background: 'var(--color-surface)', color: 'var(--color-text)', border: '1px solid var(--color-border)',
    boxShadow: '0 6px 20px rgba(0,0,0,.18)', fontSize: 12.5, lineHeight: 1.45, whiteSpace: 'pre-wrap', textAlign: 'left',
  }
  return (
    <span ref={ref} aria-describedby={tip ? describedBy : undefined} tabIndex={tip ? 0 : undefined}
      // Hover is a MOUSE thing: a touch tap also fires compatibility mouse events, and the mouseleave
      // that some browsers send after the tap closed the tooltip the tap had just opened (L16).
      onPointerEnter={tip ? e => { if (e.pointerType === 'mouse') open() } : undefined}
      onPointerLeave={tip ? e => { if (e.pointerType === 'mouse') close() } : undefined}
      onFocus={tip ? open : undefined} onBlur={tip ? close : undefined}
      onPointerDown={e => { touch.current = e.pointerType === 'touch'; openAtDown.current = !!at }}
      onClick={tip ? e => {
        const wasTouch = touch.current
        touch.current = false   // a later click with no pointer event first (assistive tech) is not a tap
        if (!wasTouch) return
        e.stopPropagation(); if (openAtDown.current) close(); else open()
      } : undefined}
      style={{ cursor: tip ? 'help' : undefined, borderBottom: cut ? '1px dotted var(--color-text-mute)' : undefined }}>
      {shown}
      {/* Screen readers get the complete text whatever the pointer does (visually hidden). */}
      {tip && <span id={describedBy} style={SR_ONLY}>{label}: {heading ? `${heading}. ` : ''}{text}</span>}
      {tip && at && (
        <span role="tooltip" style={box}>
          {heading && <span style={{ display: 'block', fontSize: 11.5, color: 'var(--color-text-mute)', marginBottom: 4 }}>{heading}</span>}
          {text}
        </span>
      )}
    </span>
  )
}
